import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { formatMoney } from '@/format/money'
import { currentLocale } from '@/i18n'
import {
  cancelDocument,
  fetchDocument,
  fetchReissues,
  fetchSignature,
  issueDocument,
  lineArithmetic,
  linesReconcile,
  markSentByEdi,
  reissueDocument,
  signDocument,
  type PrimaryDocument,
} from './documentApi'
import type { CardTab, CardTabContext } from './types'

/**
 * Вкладки карточки первичного документа (тикет #73, ТЗ §4.3).
 *
 * ## Что здесь решается
 *
 * Первичный документ — это бумага, по которой контрагент принимает НДС к вычету, а
 * налоговая проверяет сделку. Плоской строкой списка он не описывается: у него строки с
 * построчным НДС и подпись, которая удостоверяет **содержимое**, а не идентификатор.
 *
 * Две вкладки отвечают на два вопроса в том порядке, в каком их задают: «из чего он
 * сложился» и «действителен ли он и та ли это бумага, которую подписали».
 *
 * ## Про хеш подписи на экране
 *
 * Расхождение хеша выводится **тревогой**, а не строчкой в свойствах: оно означает, что
 * документ на экране отличается от подписанного, и увидеть это должен читатель, а не тот,
 * кто догадается открыть журнал. Пропустить такое расхождение молча — ровно тот случай,
 * ради которого хеш и хранится.
 *
 * `matches === true` у неподписанного документа — это «противоречить нечему», а не «всё в
 * порядке»: экран говорит «не подписан», а не «подпись сходится».
 */

/** Три состояния запроса одним компонентом: иначе их путают в каждой вкладке заново. */
function AsyncSection<T>({
  query,
  empty,
  children,
}: {
  query: { isLoading: boolean; error: unknown; data: T | undefined }
  empty: string
  children: (data: T) => React.ReactNode
}) {
  const { t } = useApp()

  if (query.isLoading) return <p>{t('app.loading')}</p>
  if (query.error) {
    return (
      <p role="alert">
        {query.error instanceof ApiError && query.error.isForbidden
          ? t('access.denied')
          : t('app.error')}
      </p>
    )
  }
  if (query.data === undefined) return <p className="muted">{empty}</p>
  return <>{children(query.data)}</>
}

/** Деньги с двумя знаками: «1500» вместо «1500.00» на первичке читается как черновик. */
function money(value: number | null | undefined, currency?: string): string {
  // One formatter for every screen (#91); the tab has no hook here, so the stored language.
  return formatMoney(value, currency ?? null, currentLocale())
}

// ------------------------------------------------------------------ строки

/**
 * Строки документа с построчным НДС.
 *
 * Показывает сохранённые сервером суммы и **проверяет** их пересчётом, а не выводит вторую
 * колонку: две цифры рядом заставляли бы читателя решать, какой верить, а он не должен
 * оказываться перед этим вопросом. Расхождение — предупреждение.
 */
function LinesPanel({ document }: { document: PrimaryDocument }) {
  const { t } = useApp()
  const lines = document.lines ?? []

  if (lines.length === 0) {
    return <p className="muted">{t('card.doc.lines.empty')}</p>
  }

  const drifting = lines.filter(
    (line) => lineArithmetic(line).total.toFixed(2) !== line.lineTotal.toFixed(2),
  )
  const reconciles = linesReconcile(document)

  return (
    <div className="card-gap">
      {/* Итог, не равный сумме строк, назван вслух: контрагент складывает напечатанные
          строки, и молчание об этом расхождении обнаружит он, а не мы. */}
      {!reconciles && (
        <p className="notice notice-error" role="alert">
          {t('card.doc.lines.mismatch')}
        </p>
      )}
      {drifting.length > 0 && (
        <p className="notice notice-error" role="alert">
          {t('card.doc.lines.lineMismatch', { count: drifting.length })}
        </p>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>{t('card.doc.line.description')}</th>
            <th>{t('card.doc.line.quantity')}</th>
            <th>{t('card.doc.line.price')}</th>
            <th>{t('card.doc.line.vatRate')}</th>
            <th>{t('card.doc.line.vat')}</th>
            <th>{t('card.doc.line.total')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id}>
              <td>{line.lineOrder}</td>
              <td>{line.description}</td>
              <td>{line.quantity}</td>
              <td>{money(line.unitPrice)}</td>
              <td>{line.vatRate}%</td>
              <td>{money(line.vatAmount)}</td>
              <td>{money(line.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5}>{t('card.doc.subtotal')}</td>
            <td>{money(document.vatTotal)}</td>
            <td>{money(document.total, document.currency)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

// ------------------------------------------------------------------ подпись и статус

/**
 * Подпись, целостность и действия над документом.
 *
 * Порядок на экране — порядок вопросов: сначала «та ли это бумага», потом «что с ней
 * можно сделать». Расхождение хеша стоит выше кнопок намеренно: действие над документом,
 * который правили после подписи, — это то, о чём надо знать до нажатия.
 */
function SignaturePanel({ documentId }: { documentId: string }) {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const [ediMessageId, setEdiMessageId] = useState('')
  const [reissueReason, setReissueReason] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  const documentQuery = useQuery({
    queryKey: ['document', documentId],
    queryFn: ({ signal }) => fetchDocument(documentId, signal),
  })

  const signatureQuery = useQuery({
    queryKey: ['document', documentId, 'signature'],
    queryFn: ({ signal }) => fetchSignature(documentId, signal),
  })

  const reissuesQuery = useQuery({
    queryKey: ['document', documentId, 'reissues'],
    queryFn: ({ signal }) => fetchReissues(documentId, signal),
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['document', documentId] })
  }

  const onFailure = (error: unknown) => {
    setFailure(error instanceof ApiError ? error.message : t('app.error'))
  }

  const issue = useMutation({
    mutationFn: () => issueDocument(documentId),
    onSuccess: () => {
      setFailure(null)
      invalidate()
    },
    onError: onFailure,
  })

  const sign = useMutation({
    mutationFn: () => signDocument(documentId),
    onSuccess: () => {
      setFailure(null)
      invalidate()
    },
    onError: onFailure,
  })

  const edi = useMutation({
    mutationFn: () => markSentByEdi(documentId, ediMessageId),
    onSuccess: () => {
      setFailure(null)
      setEdiMessageId('')
      invalidate()
    },
    onError: onFailure,
  })

  const cancel = useMutation({
    mutationFn: () => cancelDocument(documentId),
    onSuccess: () => {
      setFailure(null)
      invalidate()
    },
    onError: onFailure,
  })

  const reissue = useMutation({
    mutationFn: () => reissueDocument(documentId, reissueReason),
    onSuccess: () => {
      setFailure(null)
      setReissueReason('')
      invalidate()
    },
    onError: onFailure,
  })

  // Права проверяются и здесь, и на сервере. Здесь — чтобы не показывать кнопку,
  // ведущую к гарантированному 403: кнопка, которая всегда ошибается, учит игнорировать
  // ошибки. Запрет живёт на сервере (@PreAuthorize), и это единственная защита.
  const mayUpdate = can(user, 'billing.document', 'update')
  const mayApprove = can(user, 'billing.document', 'approve')
  const mayCreate = can(user, 'billing.document', 'create')

  return (
    <div className="card-gap">
      <AsyncSection query={documentQuery} empty={t('card.doc.empty')}>
        {(document) => (
          <>
            <AsyncSection query={signatureQuery} empty={t('card.doc.sign.unknown')}>
              {(check) => {
                if (!check.signed) {
                  // «Подписи нет» и «подпись не сходится» — разные факты, и свести их
                  // значило бы выставить каждый черновик подделанным.
                  return <p className="muted">{t('card.doc.sign.unsigned')}</p>
                }
                return check.matches ? (
                  <p className="notice">{t('card.doc.sign.intact')}</p>
                ) : (
                  <div className="notice notice-error" role="alert">
                    <p>
                      <strong>{t('card.doc.sign.mismatch')}</strong>
                    </p>
                    <p>{t('card.doc.sign.mismatchExplained')}</p>
                    <p className="muted">
                      {t('card.doc.sign.signedHash')}: <code>{check.signedHash}</code>
                    </p>
                    <p className="muted">
                      {t('card.doc.sign.currentHash')}: <code>{check.currentHash}</code>
                    </p>
                  </div>
                )
              }}
            </AsyncSection>

            <dl className="card-fields">
              <dt>{t('card.doc.status')}</dt>
              <dd>{t(`card.doc.status.${document.status}`)}</dd>
              <dt>{t('card.doc.signedAt')}</dt>
              <dd>{document.signedAt ?? '—'}</dd>
              <dt>{t('card.doc.edi')}</dt>
              <dd>{document.ediMessageId ?? t('card.doc.edi.none')}</dd>
              {document.reissuedFromId && (
                <>
                  <dt>{t('card.doc.reissuedFrom')}</dt>
                  <dd>{document.reissueReason ?? '—'}</dd>
                </>
              )}
            </dl>

            {failure && (
              <p className="notice notice-error" role="alert">
                {failure}
              </p>
            )}

            <div className="card-actions">
              {mayUpdate && document.status === 'draft' && (
                <button type="button" onClick={() => issue.mutate()} disabled={issue.isPending}>
                  {t('card.doc.action.issue')}
                </button>
              )}
              {mayApprove && document.status === 'issued' && (
                <button type="button" onClick={() => sign.mutate()} disabled={sign.isPending}>
                  {t('card.doc.action.sign')}
                </button>
              )}
              {mayUpdate && (document.status === 'issued' || document.status === 'signed') && (
                <>
                  <input
                    type="text"
                    value={ediMessageId}
                    placeholder={t('card.doc.edi.messageId')}
                    aria-label={t('card.doc.edi.messageId')}
                    onChange={(event) => setEdiMessageId(event.target.value)}
                  />
                  {/* Идентификатор обязателен: отметка без него утверждает факт, который
                      нечем проверить, — а по нему потом ищут, дошёл ли документ. */}
                  <button
                    type="button"
                    onClick={() => edi.mutate()}
                    disabled={edi.isPending || ediMessageId.trim() === ''}
                  >
                    {t('card.doc.action.edi')}
                  </button>
                </>
              )}
              {mayUpdate && document.status !== 'cancelled' && document.status !== 'signed' && (
                <button type="button" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                  {t('card.doc.action.cancel')}
                </button>
              )}
            </div>

            {mayCreate && document.status !== 'draft' && (
              <div className="card-actions">
                <input
                  type="text"
                  value={reissueReason}
                  placeholder={t('card.doc.reissue.reason')}
                  aria-label={t('card.doc.reissue.reason')}
                  onChange={(event) => setReissueReason(event.target.value)}
                />
                {/* Причина обязательна и заглушкой не подставляется: перевыпуск без неё
                    неотличим от постороннего документа с теми же строками. */}
                <button
                  type="button"
                  onClick={() => reissue.mutate()}
                  disabled={reissue.isPending || reissueReason.trim() === ''}
                >
                  {t('card.doc.action.reissue')}
                </button>
              </div>
            )}
          </>
        )}
      </AsyncSection>

      <AsyncSection query={reissuesQuery} empty={t('card.doc.reissues.empty')}>
        {(chain) =>
          chain.length === 0 ? (
            <p className="muted">{t('card.doc.reissues.empty')}</p>
          ) : (
            <table className="data-table">
              <caption>{t('card.doc.reissues')}</caption>
              <thead>
                <tr>
                  <th>{t('card.doc.number')}</th>
                  <th>{t('card.doc.date')}</th>
                  <th>{t('card.doc.reissue.reason')}</th>
                  <th>{t('card.doc.status')}</th>
                </tr>
              </thead>
              <tbody>
                {chain.map((replacement) => (
                  <tr key={replacement.id}>
                    <td>{replacement.number}</td>
                    <td>{replacement.documentDate}</td>
                    <td>{replacement.reissueReason ?? '—'}</td>
                    <td>{t(`card.doc.status.${replacement.status}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </AsyncSection>
    </div>
  )
}

/** Строки документа: из чего он сложился. */
export function documentLinesTab(): CardTab {
  return {
    id: 'doc-lines',
    labelKey: 'card.tab.docLines',
    render: (context: CardTabContext) => <DocumentLines recordId={context.recordId} />,
  }
}

function DocumentLines({ recordId }: { recordId: string }) {
  const { t } = useApp()
  const query = useQuery({
    queryKey: ['document', recordId],
    queryFn: ({ signal }) => fetchDocument(recordId, signal),
  })

  return (
    <AsyncSection query={query} empty={t('card.doc.empty')}>
      {(document) => <LinesPanel document={document} />}
    </AsyncSection>
  )
}

/** Подпись, целостность содержимого и действия. */
export function documentSignatureTab(): CardTab {
  return {
    id: 'doc-signature',
    labelKey: 'card.tab.docSignature',
    render: (context: CardTabContext) => <SignaturePanel documentId={context.recordId} />,
  }
}

/** Обе вкладки карточки первичного документа в порядке, в каком их открывают. */
export function documentTabs(): CardTab[] {
  return [documentLinesTab(), documentSignatureTab()]
}
